import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { CalendarIcon, ArrowRight, ArrowLeft, Check, FileText } from "lucide-react";

const TIME_SLOTS = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00"];
// Hoisted — never changes so we don't recreate the object on every render.
const CAL_MODIFIER_CLASSES = { blocked: "text-destructive/50 line-through" };

export default function Booking() {
  const [params] = useSearchParams();
  const preSlug = params.get("service");
  const navigate = useNavigate();

  const [services, setServices] = useState([]);
  const [step, setStep] = useState(1);
  const [blockedDates, setBlockedDates] = useState(new Set());
  const [blockedSlots, setBlockedSlots] = useState({}); // { "YYYY-MM-DD": ["09:00",...] }
  const { settings } = useSiteSettings();

  const [serviceId, setServiceId] = useState("");
  const [tierName, setTierName] = useState("");
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [details, setDetails] = useState({ name: "", email: "", phone: "", notes: "" });
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [zelle, setZelle] = useState(null);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    api.get("/config").then((r) => setZelle(r.data));
    api.get("/availability").then((r) => {
      setBlockedDates(new Set(r.data.blocked_dates || []));
      setBlockedSlots(r.data.blocked_slots || {});
    });
  }, []);

  // When the user advances or goes back a step, scroll the page back to the top
  // so the next step is in view (otherwise the mobile sticky bar / footer obscure it).
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  useEffect(() => {
    if (preSlug && services.length) {
      const s = services.find((x) => x.slug === preSlug);
      if (s) setServiceId(s.id);
    }
  }, [preSlug, services]);

  const service = useMemo(() => services.find((s) => s.id === serviceId), [serviceId, services]);
  const tier = useMemo(() => service?.tiers.find((t) => t.name === tierName), [service, tierName]);
  const depositInfo = useMemo(() => {
    if (!service || !tier) return null;
    const pct = Number(service.deposit_percent) || 25;
    return { amount: Math.round(tier.price * pct) / 100, percent: pct };
  }, [service, tier]);

  const canNext = {
    1: Boolean(serviceId && tierName),
    2: Boolean(date && time),
    3: Boolean(details.name && details.email && details.phone && policyAccepted),
  };

  async function submit() {
    if (!canNext[3]) return;
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        service_id: serviceId,
        tier_name: tierName,
        session_date: format(date, "yyyy-MM-dd"),
        session_time: time,
        customer_name: details.name,
        customer_email: details.email,
        customer_phone: details.phone,
        notes: details.notes,
        policy_accepted: policyAccepted,
      });
      toast.success("Booking received");
      navigate(`/booking/confirmation/${data.reference}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit booking");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container-x py-14 md:py-28">
      <div className="max-w-3xl">
        <div className="eyebrow mb-3 md:mb-4">Reserve your session</div>
        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light leading-[1.05]">Book a Session</h1>
      </div>

      <Stepper step={step} />

      <div className="grid lg:grid-cols-12 gap-8 md:gap-10 mt-8 md:mt-10 lg:pb-0 pb-32">
        <div className="lg:col-span-8 order-2 lg:order-1">
          {step === 1 && (
            <Step1 services={services} serviceId={serviceId} setServiceId={(id) => { setServiceId(id); setTierName(""); }} tierName={tierName} setTierName={setTierName} canNext={canNext[1]} onContinue={() => setStep(2)} />
          )}
          {step === 2 && <Step2 date={date} setDate={setDate} time={time} setTime={setTime} blockedDates={blockedDates} blockedSlots={blockedSlots} />}
          {step === 3 && <Step3 details={details} setDetails={setDetails} zelle={zelle} service={service} tier={tier} depositInfo={depositInfo} policy={settings?.booking_policy || ""} policyAccepted={policyAccepted} setPolicyAccepted={setPolicyAccepted} />}

          <div className="hidden lg:flex mt-8 md:mt-10 items-center justify-between">
            <button
              type="button"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground disabled:opacity-30"
              data-testid="booking-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            {step < 3 ? (
              <button
                type="button"
                onClick={() => setStep((s) => s + 1)}
                disabled={!canNext[step]}
                className="inline-flex items-center gap-3 px-6 sm:px-7 h-11 sm:h-12 bg-primary text-primary-foreground text-xs uppercase tracking-[0.22em] hover:bg-primary/90 disabled:opacity-40"
                data-testid="booking-next-btn"
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={!canNext[3] || loading}
                className="inline-flex items-center gap-3 px-7 sm:px-8 h-11 sm:h-12 bg-primary text-primary-foreground text-xs uppercase tracking-[0.22em] hover:bg-primary/90 disabled:opacity-50"
                data-testid="booking-submit-btn"
              >
                {loading ? "Submitting…" : "Confirm Booking"} <Check className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <aside className="lg:col-span-4 order-1 lg:order-2">
          <Summary service={service} tier={tier} date={date} time={time} depositInfo={depositInfo} />
        </aside>
      </div>

      {/* Mobile-only sticky bottom action bar — keeps Continue reachable without scrolling.
          Uses safe-area-inset-bottom so it sits above the iOS home indicator / Safari bottom bar.
          touchAction: manipulation kills the 300ms tap-delay on Android Chrome. */}
      <div
        className="lg:hidden fixed inset-x-0 bottom-0 z-50 bg-background border-t border-border shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.18)] flex items-center justify-between gap-3 px-4 py-3"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
        data-testid="booking-mobile-actions"
      >
        <button
          type="button"
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          disabled={step === 1}
          style={{ touchAction: "manipulation" }}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground disabled:opacity-30 px-2 py-2"
          data-testid="booking-back-btn-mobile"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        {step < 3 ? (
          <button
            type="button"
            onClick={() => setStep((s) => s + 1)}
            disabled={!canNext[step]}
            style={{ touchAction: "manipulation" }}
            className="inline-flex flex-1 max-w-[260px] items-center justify-center gap-3 h-12 bg-primary text-primary-foreground text-xs uppercase tracking-[0.22em] hover:bg-primary/90 active:opacity-90 disabled:opacity-40"
            data-testid="booking-next-btn-mobile"
          >
            Continue <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={!canNext[3] || loading}
            style={{ touchAction: "manipulation" }}
            className="inline-flex flex-1 max-w-[260px] items-center justify-center gap-3 h-12 bg-primary text-primary-foreground text-xs uppercase tracking-[0.22em] hover:bg-primary/90 active:opacity-90 disabled:opacity-50"
            data-testid="booking-submit-btn-mobile"
          >
            {loading ? "Submitting…" : "Confirm Booking"} <Check className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function Stepper({ step }) {
  const steps = ["Package", "Date & Time", "Details & Payment"];
  return (
    <div className="mt-10 md:mt-12 -mx-6 md:mx-0 px-6 md:px-0 overflow-x-auto" data-testid="booking-stepper">
      <div className="flex items-center gap-3 md:gap-6 min-w-max">
        {steps.map((s, i) => {
          const idx = i + 1;
          const active = step === idx;
          const done = step > idx;
          return (
            <div key={s} className="flex items-center gap-2 md:gap-3 shrink-0">
              <div
                className={`w-7 h-7 flex items-center justify-center text-[11px] border ${
                  active ? "border-foreground bg-foreground text-background" : done ? "border-foreground" : "border-border text-muted-foreground"
                }`}
              >
                {done ? <Check className="w-3.5 h-3.5" /> : idx}
              </div>
              <span className={`text-[10px] md:text-xs uppercase tracking-[0.18em] md:tracking-[0.22em] ${active ? "text-foreground" : "text-muted-foreground"}`}>{s}</span>
              {i < steps.length - 1 && <div className="w-6 md:w-10 h-px bg-border" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Step1({ services, serviceId, setServiceId, tierName, setTierName, canNext, onContinue }) {
  const service = services.find((s) => s.id === serviceId);
  const tierPanelRefs = useRef({});

  // When a service is tapped on mobile, scroll the inline tier panel into view so the
  // user immediately sees the tier options + inline Continue button without scrolling.
  useEffect(() => {
    if (!serviceId) return;
    const el = tierPanelRefs.current[serviceId];
    if (el && window.matchMedia("(max-width: 767px)").matches) {
      // Tiny delay so the panel finishes mounting before we scroll to it
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    }
  }, [serviceId]);

  return (
    <div>
      <div className="eyebrow mb-4">Choose your session & tier</div>
      <div className="grid md:grid-cols-12 gap-6 md:gap-8">
        {/* Services list (left). On mobile, the tier panel is rendered inline right under the selected card. */}
        <div className="md:col-span-6 lg:col-span-7 space-y-3">
          {services.map((s) => {
            const selected = s.id === serviceId;
            return (
              <div key={s.id}>
                <button
                  type="button"
                  onClick={() => setServiceId(s.id)}
                  className={`w-full text-left border p-3 sm:p-4 transition-colors flex gap-3 sm:gap-4 items-center ${
                    selected ? "border-foreground bg-card" : "border-border hover:border-foreground/50"
                  }`}
                  data-testid={`select-service-${s.slug}`}
                >
                  <img src={s.image_url} alt={s.name} className="w-14 h-18 sm:w-16 sm:h-20 object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="eyebrow">{s.category}</div>
                    <div className="font-serif text-lg mt-1">{s.name}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      from ${Math.min(...s.tiers.map((t) => t.price)).toFixed(0)} · {s.tiers.length} tier{s.tiers.length === 1 ? "" : "s"}
                    </div>
                  </div>
                  {selected && <Check className="w-4 h-4 text-foreground shrink-0" />}
                </button>

                {/* Mobile-only inline tier panel — appears immediately under the tapped card */}
                {selected && (
                  <div
                    ref={(el) => { tierPanelRefs.current[s.id] = el; }}
                    className="md:hidden mt-3 animate-fade-up scroll-mt-24"
                    data-testid="tier-panel-mobile"
                  >
                    <TierPanel service={service} tierName={tierName} setTierName={setTierName} canNext={canNext} onContinue={onContinue} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Tiers (right) — desktop only, sticky */}
        <div className="hidden md:block md:col-span-6 lg:col-span-5 md:sticky md:top-28 self-start">
          {!service ? (
            <div
              className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground"
              data-testid="tier-empty-state"
            >
              <div className="font-serif text-2xl text-foreground/40 mb-2">Select a session</div>
              Choose a session on the left to see its tiers.
            </div>
          ) : (
            <TierPanel service={service} tierName={tierName} setTierName={setTierName} canNext={canNext} onContinue={onContinue} />
          )}
        </div>
      </div>
    </div>
  );
}

function TierPanel({ service, tierName, setTierName, canNext, onContinue }) {
  const continueBtnRef = useRef(null);

  // When tier is picked on mobile, scroll the Continue button into view so it's clearly
  // visible above the sticky bottom action bar (which can otherwise cover it on small screens).
  useEffect(() => {
    if (!canNext) return;
    if (window.matchMedia("(max-width: 767px)").matches && continueBtnRef.current) {
      setTimeout(() => {
        continueBtnRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 80);
    }
  }, [canNext, tierName]);

  if (!service) return null;
  return (
    <div className="border border-border bg-card" data-testid="tier-panel">
      <div className="p-5 border-b border-border">
        <div className="eyebrow">{service.category}</div>
        <div className="font-serif text-2xl mt-1">{service.name}</div>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{service.description}</p>
      </div>
      <ul>
        {service.tiers.map((t) => {
          const selectedTier = tierName === t.name;
          return (
            <li key={t.name}>
              <button
                type="button"
                onClick={() => setTierName(t.name)}
                className={`w-full text-left px-5 py-4 border-b border-border last:border-b-0 flex items-start justify-between gap-4 transition-colors ${
                  selectedTier ? "bg-accent/40" : "hover:bg-muted"
                }`}
                data-testid={`select-tier-${t.name.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-serif text-lg">{t.name}</div>
                    {selectedTier && <Check className="w-3.5 h-3.5" />}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{t.description}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-serif text-xl">${t.price.toFixed(0)}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    {t.duration_minutes} min
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
      {/* Inline Continue button — appears the moment a tier is picked, right under the tier list
          so the user doesn't have to look for it. Adaptive label: "Continue with {tier}".
          touchAction: manipulation eliminates the 300ms tap-delay on Android Chrome. */}
      {canNext && (
        <div className="p-4 border-t border-border bg-muted/30 animate-fade-up">
          <button
            ref={continueBtnRef}
            type="button"
            onClick={onContinue}
            style={{ touchAction: "manipulation" }}
            className="w-full inline-flex items-center justify-center gap-2 h-12 bg-primary text-primary-foreground text-xs uppercase tracking-[0.22em] hover:bg-primary/90 active:opacity-90 relative z-[60]"
            data-testid="tier-continue-btn"
          >
            Continue with {tierName} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

function Step2({ date, setDate, time, setTime, blockedDates, blockedSlots }) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  function isBlocked(d) {
    const iso = format(d, "yyyy-MM-dd");
    return blockedDates && blockedDates.has(iso);
  }
  // Stable reference: the modifier object only changes when the blocked set or function identity does.
  const calendarModifiers = useMemo(() => ({ blocked: isBlocked }), [blockedDates]); // eslint-disable-line react-hooks/exhaustive-deps
  const dateISO = date ? format(date, "yyyy-MM-dd") : null;
  const slotsBlockedForDate = new Set((dateISO && blockedSlots && blockedSlots[dateISO]) || []);

  return (
    <div className="space-y-10">
      <div>
        <div className="eyebrow mb-4">Pick a date</div>
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-3 px-5 h-12 border border-border hover:border-foreground/60 text-sm"
              data-testid="booking-date-trigger"
            >
              <CalendarIcon className="w-4 h-4" />
              {date ? format(date, "PPP") : "Choose date"}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-0">
            <Calendar
              mode="single"
              selected={date}
              onSelect={(d) => {
                setDate(d);
                setTime("");
                // Auto-close once a valid date is picked
                if (d) setCalendarOpen(false);
              }}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || isBlocked(d)}
              modifiers={calendarModifiers}
              modifiersClassNames={CAL_MODIFIER_CLASSES}
              initialFocus
            />
            <div className="px-4 pb-3 text-[11px] text-muted-foreground">
              Dates with a line through them are unavailable.
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <div>
        <div className="eyebrow mb-4">Pick a time</div>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
          {TIME_SLOTS.map((t) => {
            const slotBlocked = slotsBlockedForDate.has(t);
            return (
              <button
                key={t}
                type="button"
                disabled={slotBlocked}
                onClick={() => !slotBlocked && setTime(t)}
                className={`h-11 border text-sm transition-colors ${
                  slotBlocked
                    ? "border-border text-muted-foreground/50 line-through cursor-not-allowed bg-muted/40"
                    : time === t
                    ? "border-foreground bg-foreground text-background"
                    : "border-border hover:border-foreground/60"
                }`}
                data-testid={`time-slot-${t}`}
              >
                {t}
              </button>
            );
          })}
        </div>
        {slotsBlockedForDate.size > 0 && (
          <div className="mt-3 text-xs text-muted-foreground">
            Some time slots on this day are unavailable.
          </div>
        )}
      </div>
    </div>
  );
}

function Step3({ details, setDetails, zelle, service, tier, depositInfo, policy, policyAccepted, setPolicyAccepted }) {
  const deposit = depositInfo?.amount ?? 0;
  const depositPct = depositInfo?.percent ?? 0;
  return (
    <div className="space-y-10">
      <div className="grid sm:grid-cols-2 gap-6">
        <Input label="Full name" testid="customer-name" value={details.name} onChange={(v) => setDetails({ ...details, name: v })} required />
        <Input label="Email" testid="customer-email" type="email" value={details.email} onChange={(v) => setDetails({ ...details, email: v })} required />
        <Input label="Phone" testid="customer-phone" value={details.phone} onChange={(v) => setDetails({ ...details, phone: v })} required />
        <div className="sm:col-span-2">
          <label className="block">
            <span className="eyebrow">Notes (optional)</span>
            <textarea
              rows={3}
              value={details.notes}
              onChange={(e) => setDetails({ ...details, notes: e.target.value })}
              className="mt-2 w-full bg-transparent border border-border p-3 outline-none focus:border-foreground resize-none"
              data-testid="customer-notes"
            />
          </label>
        </div>
      </div>

      <div className="border border-accent bg-card p-5 sm:p-6" data-testid="zelle-instructions">
        <div className="eyebrow mb-2">Zelle deposit · secures your date</div>
        <h3 className="font-serif text-2xl">
          Pay your {depositPct}% deposit (${deposit.toFixed(2)}) via Zelle
        </h3>
        <p className="text-sm text-muted-foreground mt-2">Once we receive your deposit, we'll confirm your session by email.</p>
        <div className="grid sm:grid-cols-2 gap-4 mt-5 text-sm">
          <Detail label="Send to (Zelle)" value={zelle?.zelle_email || "payments@allureimage.com"} />
          <Detail label="Recipient name" value={zelle?.zelle_name || "Allure Image Photography"} />
          <Detail label="Deposit amount" value={`$${deposit.toFixed(2)} (${depositPct}% of $${tier?.price.toFixed(2)})`} />
          <Detail label="Memo / Reference" value="(use the reference shown on the next page)" />
        </div>
      </div>

      <div className="border border-border bg-card" data-testid="policy-block">
        <div className="flex items-center gap-3 p-4 sm:p-5 border-b border-border">
          <FileText className="w-4 h-4" />
          <div className="font-serif text-xl">Studio policy & rules</div>
        </div>
        <div className="px-5 py-4 max-h-72 overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed text-foreground/80" data-testid="policy-text">
          {policy || "Please read our studio policy before booking."}
        </div>
        <label className="flex items-start gap-3 p-5 border-t border-border cursor-pointer hover:bg-muted/40">
          <input
            type="checkbox"
            checked={policyAccepted}
            onChange={(e) => setPolicyAccepted(e.target.checked)}
            className="mt-1 h-4 w-4 accent-foreground"
            data-testid="policy-checkbox"
          />
          <span className="text-sm">
            <strong>I have read and agree to the studio policy and rules above.</strong>
            <div className="text-xs text-muted-foreground mt-0.5">You must accept to complete your booking.</div>
          </span>
        </label>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, required, type = "text", testid }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}{required && <span className="text-foreground">*</span>}</span>
      <input
        type={type}
        required={required}
        value={value}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-transparent border-b border-border py-3 outline-none focus:border-foreground"
      />
    </label>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div className="mt-1 text-foreground">{value}</div>
    </div>
  );
}

function Summary({ service, tier, date, time, depositInfo }) {
  return (
    <div className="border border-border p-5 md:p-6 lg:sticky lg:top-28 bg-card" data-testid="booking-summary">
      <div className="eyebrow mb-3 md:mb-4">Your session</div>
      <Row label="Service" value={service?.name || "—"} />
      <Row label="Tier" value={tier?.name || "—"} />
      <Row label="Date" value={date ? format(date, "PPP") : "—"} />
      <Row label="Time" value={time || "—"} />
      <div className="my-4 h-px bg-border" />
      <Row label="Total" value={tier ? `$${tier.price.toFixed(2)}` : "—"} bold />
      <Row
        label={`Deposit (${depositInfo?.percent || 0}%)`}
        value={depositInfo ? `$${depositInfo.amount.toFixed(2)}` : "—"}
      />
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-baseline justify-between py-2">
      <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</span>
      <span className={`text-sm ${bold ? "font-serif text-xl" : ""}`}>{value}</span>
    </div>
  );
}
